import {
  cacheKeys,
  FeatureName,
  Group,
  useAllWorkspaceGroups,
  useCreateGroup,
  useCurrentWorkspace,
  useCustomMutation,
  useIsFeatureEnabled,
} from '@gripp/shared-logic';
import {
  ColumnData,
  SelectionStatus,
  SlideLayout,
  ViewMode,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Accounts as AccountsOld } from './accounts-old';
import { AccountsComponent, SelectedAccountRow } from './accountsComponent';
import { AccountsFilters } from './accountsFilters';
import { AccountsGroupsPicker } from './accountsGroupsPicker';
import {
  AccountsSelectionPanelHeaderActions,
  AccountsSelectionPanelMain,
} from './accountsSelectionPanel';

export const Accounts = () => {
  const isCropCircleEnabled = useIsFeatureEnabled(FeatureName.CropCircleRedesign);

  if (isCropCircleEnabled) {
    return <AccountsBase />;
  }

  return <AccountsOld />;
};

export const AccountsBase = ({
  rightColumn: rightColumnOverride,
  viewMode: viewModeOverride,
}: {
  rightColumn?: ColumnData;
  viewMode?: ViewMode;
} = {}) => {
  const { t, i18n } = useTranslation();
  const { workspaceId: paramWorkspaceId } = useParams();
  const workspace = useCurrentWorkspace(true);
  const queryClient = useQueryClient();
  const { createGroup } = useCreateGroup();

  const [rowsSelected, setRowsSelected] = useState<SelectedAccountRow[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<Group[]>([]);
  const [isGroupsPickerOpen, setIsGroupsPickerOpen] = useState(false);
  const [selectionStatus, setSelectionStatus] =
    useState<SelectionStatus>('idle');

  const rowsSelectedIds = useMemo(
    () => rowsSelected.map((r) => r.id),
    [rowsSelected]
  );

  const currentWorkspaceId = paramWorkspaceId ?? workspace?.id;

  const { groups: availableGroups } = useAllWorkspaceGroups(currentWorkspaceId);

  const { mutateAsync: addAccountsToGroups } = useCustomMutation({
    query: ADD_ACCOUNTS_TO_GROUPS,
  });
  const { mutateAsync: clearGroups } = useCustomMutation({
    query: CLEAR_GROUPS,
  });

  // Seed the groups panel from the selection whenever rows change:
  //   - 1 person selected  → their groups are pre-selected.
  //   - 2+ selected with exact same groups → pre-selected.
  //   - 2+ selected with any difference   → empty (user picks fresh).
  //   - 0 selected         → clear and close picker.
  //
  // The `listAccounts` projection strips group ids and only returns names, so
  // match by name and then re-hydrate the real id from `availableGroups`.
  useEffect(() => {
    if (rowsSelected.length === 0) {
      setSelectedGroups([]);
      setIsGroupsPickerOpen(false);
      return;
    }

    const [first, ...rest] = rowsSelected;
    const firstNames = (first.groups ?? [])
      .map((g) => g.name)
      .filter((name): name is string => !!name)
      .sort();

    const allSame = rest.every((row) => {
      const rowNames = (row.groups ?? [])
        .map((g) => g.name)
        .filter((name): name is string => !!name)
        .sort();
      return (
        rowNames.length === firstNames.length &&
        rowNames.every((n, i) => n === firstNames[i])
      );
    });

    if (!allSame) {
      setSelectedGroups([]);
      return;
    }

    const nextSelected = firstNames
      .map((name) => availableGroups.find((g) => g.name === name))
      .filter((g): g is Group => !!g)
      .map((g) => ({ id: g.id, name: g.name }));

    setSelectedGroups(nextSelected);
  }, [rowsSelected, availableGroups]);

  const clearSelection = useCallback(() => {
    setRowsSelected([]);
    setSelectionStatus('reset');
    rightColumnOverride?.onBackButtonPress?.();
  }, [rightColumnOverride]);

  // Additive group application. Backend only supports `addAccountsToGroups`
  // (merge) and `clearGroups` (remove all), so chip removals are display-only
  // until a `removeAccountsFromGroups` mutation is available.
  const handleGroupsChange = useCallback(
    async (nextGroups: Group[]) => {
      const previousIds = new Set(selectedGroups.map((g) => g.id));
      const added = nextGroups.filter((g) => !previousIds.has(g.id));

      setSelectedGroups(nextGroups);

      if (added.length === 0 || !currentWorkspaceId) {
        return;
      }

      await addAccountsToGroups({
        input: {
          groups: added.map((g) => ({ id: g.id })),
          accountIds: rowsSelectedIds,
          workspaceId: currentWorkspaceId,
        },
      });
      await queryClient.invalidateQueries({
        queryKey: cacheKeys('account').all,
      });
    },
    [
      selectedGroups,
      rowsSelectedIds,
      currentWorkspaceId,
      addAccountsToGroups,
      queryClient,
    ]
  );

  const handleReset = useCallback(async () => {
    if (rowsSelectedIds.length === 0) return;
    await clearGroups({ input: { accountIds: rowsSelectedIds } });
    await queryClient.invalidateQueries({
      queryKey: cacheKeys('account').all,
    });
    clearSelection();
  }, [rowsSelectedIds, clearGroups, queryClient, clearSelection]);

  const leftColumn = {
    title: 'people.menu.people',
    mainComponent: (
      <AccountsComponent
        onRowsSelectedChange={setRowsSelected}
        selectedRowIds={rowsSelectedIds}
        selectionStatus={selectionStatus}
        onSelectionStatusChange={setSelectionStatus}
      />
    ),
    headerComponent: <AccountsFilters />,
    disableScroll: true,
  };

  const selectionRightColumn = useMemo<ColumnData | undefined>(() => {
    if (rowsSelected.length === 0 || !currentWorkspaceId) {
      return undefined;
    }

    const title =
      rowsSelected.length === 1
        ? t('account.topBar.personSelected')
        : t('account.topBar.peopleSelected', { count: rowsSelected.length });

    // SlideLayout re-runs `t()` on whatever title we pass. Register the
    // already-translated string as a resource so that second pass resolves
    // cleanly instead of logging `missingKey`.
    i18n.addResource(i18n.language, 'translation', title, title);

    return {
      title,
      mainComponent: (
        <AccountsSelectionPanelMain
          selectedGroups={selectedGroups}
          onGroupsChange={handleGroupsChange}
          onCreateGroup={createGroup}
          onOpenPicker={() => setIsGroupsPickerOpen(true)}
        />
      ),
      headerComponent: (
        <AccountsSelectionPanelHeaderActions onReset={handleReset} />
      ),
      showBackButton: true,
      onBackButtonPress: clearSelection,
      disableScroll: false,
    };
  }, [
    rowsSelected,
    currentWorkspaceId,
    selectedGroups,
    handleGroupsChange,
    handleReset,
    clearSelection,
    createGroup,
    t,
    i18n,
  ]);

  const thirdColumn = useMemo(() => {
    if (
      !isGroupsPickerOpen ||
      rowsSelected.length === 0 ||
      !currentWorkspaceId
    ) {
      return undefined;
    }

    return {
      title: 'asset.groups',
      mainComponent: (
        <AccountsGroupsPicker
          selectedGroups={selectedGroups}
          onGroupsChange={handleGroupsChange}
          onCreateGroup={createGroup}
          workspaceId={currentWorkspaceId}
        />
      ),
      showBackButton: true,
      onBackButtonPress: () => setIsGroupsPickerOpen(false),
      disableScroll: true,
    };
  }, [
    isGroupsPickerOpen,
    rowsSelected,
    currentWorkspaceId,
    selectedGroups,
    handleGroupsChange,
    createGroup,
  ]);

  const resolvedRightColumn = selectionRightColumn ?? rightColumnOverride;
  const resolvedViewMode =
    viewModeOverride ??
    (selectionRightColumn || rightColumnOverride
      ? ViewMode.DUAL
      : ViewMode.SINGLE);

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={resolvedRightColumn}
      thirdColumn={thirdColumn}
      viewMode={resolvedViewMode}
    />
  );
};

const ADD_ACCOUNTS_TO_GROUPS = `
  mutation Account($input: AddAccountsToGroupsInput!) {
    account {
      addAccountsToGroups(input: $input) {
        success
        message
      }
    }
  }
`;

const CLEAR_GROUPS = `
  mutation ClearGroups($input: ClearGroupsInput!) {
    account {
      clearGroups(input: $input) {
        success
      }
    }
  }
`;
